import { useMemo } from 'react';
import { IDealGroup, IDealsResponse } from 'views/Deals/types';
import { CONFLICT_COLOR } from '../components/mapTheme';

export interface DealColorEntry {
    hexColor: string;
    groupNames: string[];
}
export type DealColorLookup = Record<number, DealColorEntry>;

export const useColoredDealIds = (
    deals: IDealsResponse | undefined,
    dealGroups: IDealGroup[] | undefined,
    selectedDealGroup: number | undefined,
): DealColorLookup => {
    const {colors, groupNamesById} = useMemo(() => {
        const colorMap: Record<number, string> = {};
        const namesMap: Record<number, string> = {};
        if (dealGroups) {
            dealGroups.forEach(group => {
                if (group.color) {
                    colorMap[group.id] = group.color;
                }
                namesMap[group.id] = group.name;
            });
        }
        return { colors: colorMap, groupNamesById: namesMap };
    }, [dealGroups]);

    // Determine which colored groups to fetch deals for
    const coloredGroupIds = useMemo(
        () => {
            if (selectedDealGroup != null) {
                return colors[selectedDealGroup] ? [selectedDealGroup] : [];
            }
            return Object.keys(colors)
                .map(Number)
                .filter(id => !Number.isNaN(id))
                .sort((a, b) => a - b);
        },
        [colors, selectedDealGroup],
    );

    // Build lookup table mapping deal IDs to their colors and group names
    const lookup: DealColorLookup = {};

    coloredGroupIds.forEach((groupId, idx) => {
        const items = deals?.items;
        const hexColor = colors[groupId];
        const groupName = groupNamesById[groupId] ?? `Group ${groupId}`;

        if (!items || !hexColor) return;

        items.forEach(deal => {
            if (!deal.dealGroups?.some(g => groupId == g.id)) return;

            const existing = lookup[deal.id];

            if (!existing) {
                lookup[deal.id] = { hexColor, groupNames: [groupName] };
                return;
            }

            if (existing.hexColor === hexColor) return;

            // Conflict detected: deal belongs to multiple groups with different colors
            lookup[deal.id] = {
                hexColor: CONFLICT_COLOR,
                groupNames: existing.groupNames.includes(groupName)
                    ? existing.groupNames
                    : [...existing.groupNames, groupName],
            };
        });
    });

    return lookup;
};
